import { useHandler } from "@redotech/react-util/hook";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { SegmentListMultipleItemMembershipOperator } from "@redotech/redo-model/checkouts/checkout-tree";
import type { MetafieldValueMatchKind } from "@redotech/redo-model/checkouts/product-metafields";
import { ProductMetafieldsSegment } from "@redotech/redo-model/post-purchase/post-purchase-flow";
import { ShopifyIdType } from "@redotech/shopify-util/id";
import { memo, useMemo, useState } from "react";
import { RedoBadge } from "../../../arbiter-components/badge/redo-badge";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../../../arbiter-components/input/redo-text-input";
import { TextRedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { OperatorSelector } from "../../operator-selector";

type Metafield = ProductMetafieldsSegment["metafields"][number];

const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "At least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "No",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "Every",
};

interface ProductMetafieldsSegmentCommonProps {
  condition: ProductMetafieldsSegment;
}

export type ProductMetafieldsSegmentProps =
  ProductMetafieldsSegmentCommonProps &
    (
      | {
          readonly: false;
          setCondition: (condition: ProductMetafieldsSegment) => void;
        }
      | { readonly: true }
    );

export const ProductMetafieldsSegmentCondition = memo(
  function ProductMetafieldsSegmentCondition({
    condition,
    ...props
  }: ProductMetafieldsSegmentProps) {
    const [namespace, setNamespace] = useState("");
    const [key, setKey] = useState("");
    const [value, setValue] = useState("");
    const [valueMatchKind, setValueMatchKind] =
      useState<MetafieldValueMatchKind>("exact");
    const [resourceType, setResourceType] = useState<
      ShopifyIdType.Product | ShopifyIdType.ProductVariant
    >(ShopifyIdType.Product);

    const resourceTypeOptions: TextRedoListItem<
      ShopifyIdType.Product | ShopifyIdType.ProductVariant
    >[] = useMemo(
      () => [
        {
          type: "text",
          id: ShopifyIdType.Product,
          value: ShopifyIdType.Product,
          text: "Product metafield",
        },
        {
          type: "text",
          id: ShopifyIdType.ProductVariant,
          value: ShopifyIdType.ProductVariant,
          text: "Variant metafield",
        },
      ],
      [],
    );

    const selectedResourceTypeOption = useMemo(() => {
      return resourceTypeOptions.find(
        (option) => option.value === resourceType,
      );
    }, [resourceType, resourceTypeOptions]);

    const matchKindOptions: TextRedoListItem<MetafieldValueMatchKind>[] =
      useMemo(
        () => [
          { type: "text", id: "exact", value: "exact", text: "Exact match" },
          {
            type: "text",
            id: "partial",
            value: "partial",
            text: "Contains (partial match)",
          },
        ],
        [],
      );

    const selectedMatchKindOption = useMemo(() => {
      return matchKindOptions.find((option) => option.value === valueMatchKind);
    }, [valueMatchKind, matchKindOptions]);

    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, cartOperator });
        }
      },
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentListMultipleItemMembershipOperator) => {
        if (!props.readonly) {
          props.setCondition({ ...condition, operator });
        }
      },
    );

    const handleAddMetafield = useHandler(() => {
      if (props.readonly) {
        return;
      }
      if (!namespace.trim() || !key.trim() || !value.trim()) {
        return;
      }

      const newMetafield: Metafield = {
        resourceType,
        namespace: namespace.trim(),
        key: key.trim(),
        value: value.trim(),
        valueMatchKind,
      };

      const exists = condition.metafields.some(
        (m) =>
          m.resourceType === newMetafield.resourceType &&
          m.namespace === newMetafield.namespace &&
          m.key === newMetafield.key &&
          m.value === newMetafield.value &&
          m.valueMatchKind === newMetafield.valueMatchKind,
      );

      if (!exists) {
        props.setCondition({
          ...condition,
          metafields: [...condition.metafields, newMetafield],
        });
      }

      setNamespace("");
      setKey("");
      setValue("");
      setValueMatchKind("exact");
    });

    const handleRemoveMetafield = useHandler((index: number) => {
      if (!props.readonly) {
        props.setCondition({
          ...condition,
          metafields: condition.metafields.filter((_, i) => i !== index),
        });
      }
    });

    return (
      <>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleCartOperatorSelected}
          operatorToText={segmentListMultipleItemMembershipOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.cartOperator}
        />
        <Text fontSize="xs" textColor="primary">
          item in the cart has
        </Text>
        <OperatorSelector<SegmentListMultipleItemMembershipOperator>
          availableOptions={Object.values(
            SegmentListMultipleItemMembershipOperator,
          )}
          onOperatorSelected={handleOperatorSelected}
          operatorToText={segmentListMultipleItemMembershipOperatorToText}
          readonly={props.readonly}
          selectedOperator={condition.operator}
        />
        <Text fontSize="xs" textColor="primary">
          of these metafields:
        </Text>

        <Flex dir="row" gap="md" w="full" wrap="wrap">
          {condition.metafields.length === 0 ? (
            <Text fontSize="xs" textColor="tertiary">
              No metafields configured
            </Text>
          ) : (
            condition.metafields.map((metafield, index) => (
              <RedoBadge
                color="gray"
                iconTrailing={
                  props.readonly
                    ? undefined
                    : {
                        type: "icon",
                        onClick: () => handleRemoveMetafield(index),
                        Icon: TrashIcon,
                      }
                }
                key={index}
                size="sm"
                text={`${metafield.resourceType === ShopifyIdType.Product ? "Product" : "Variant"}: ${metafield.namespace}.${metafield.key} = ${metafield.value} (${metafield.valueMatchKind === "partial" ? "contains" : "exact"})`}
              />
            ))
          )}
        </Flex>

        {!props.readonly && (
          <Flex dir="column" gap="sm" w="full">
            <RedoSingleSelectDropdownInput<
              ShopifyIdType.Product | ShopifyIdType.ProductVariant
            >
              label="Resource type"
              options={resourceTypeOptions}
              optionSelected={({ item }) => setResourceType(item.value)}
              selectedItem={selectedResourceTypeOption}
              size="xs"
            />
            <RedoTextInput
              label="Namespace"
              setValue={setNamespace}
              size="sm"
              value={namespace}
            />
            <RedoTextInput
              label="Key"
              setValue={setKey}
              size="sm"
              value={key}
            />
            <RedoTextInput
              label="Value"
              setValue={setValue}
              size="sm"
              value={value}
            />
            <RedoSingleSelectDropdownInput<MetafieldValueMatchKind>
              label="Match type"
              options={matchKindOptions}
              optionSelected={({ item }) => setValueMatchKind(item.value)}
              selectedItem={selectedMatchKindOption}
              size="xs"
            />
            <RedoButton
              disabled={!namespace.trim() || !key.trim() || !value.trim()}
              hierarchy="secondary"
              onClick={handleAddMetafield}
              size="xs"
              text="Add metafield"
            />
          </Flex>
        )}
      </>
    );
  },
);
